import { useCallback, useMemo, useState } from 'react';
import type { WebViewMessageEvent } from 'react-native-webview';
import {
  logger as LOG,
  useNoticeBannersStackContext,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

// ────────────────────────────────────────────────────────────────────────────────
LOG.enable('BRAINTREE');
const logger = LOG.extend('BRAINTREE');

export const useHostedFieldsPlaceholders = () => {
  const { t } = useLocalizationContext();

  return {
    numberPlaceholder: t('credit-card-form.card-number.placeholder'),
    expirationDatePlaceholder: t(
      'credit-card-form.expiration-date.placeholder',
    ),
    cvvPlaceholder: t('credit-card-form.cvv.placeholder'),
    postalCodePlaceholder: t('credit-card-form.zip-code.placeholder'),
  };
};

export const useHostedFieldsErrors = () => {
  const { t } = useLocalizationContext();

  return {
    numberError: t('credit-card-form.error.inline.number'),
    expirationDateError: t('credit-card-form.error.inline.expiration'),
    cvvError: t('credit-card-form.error.inline.cvv'),
    postalCodeError: t('credit-card-form.error.inline.zipCode'),
  };
};

export const useBraintreeMessages = ({
  isEditing,
  onError,
  onSuccess,
}: BraintreeMessagesHandlerProps) => {
  const [isLoadingFields, setLoadingFields] = useState(true);
  const [isTokenizing, setIsTokenizing] = useState(false);
  const [hasCardNumber, setHasCardNumber] = useState(Boolean(isEditing));
  const [hasExpDate, setHasExpDate] = useState(Boolean(isEditing));
  const [hasCvv, setHasCvv] = useState(Boolean(isEditing));
  const [hasPostalCode, setHasPostalCode] = useState(Boolean(isEditing));
  const [canSubmit, setCanSubmit] = useState(Boolean(isEditing));
  const handleBraintreeError = useBraintreeErrorHandling({ onError });

  const handleBraintreeMessages = useCallback(
    (event: WebViewMessageEvent) => {
      const {
        nonce: receivedNonce,
        error,
        loaded,
        saving,
        fields,
        log,
      } = parseBraintreeMessage(event);

      // Clear tokenizing flag.
      setIsTokenizing(false);

      // Generic logging data from braintree.
      if (log) logger.info(log);

      // Error handling from braintree (Banner + Telemetry).
      if (error) handleBraintreeError(error);

      // Fields are ready to be used.
      if (loaded) setLoadingFields(false);

      // Credit card is being tokenized.
      if (saving) setIsTokenizing(true);

      // Update validity based on form content, toggles submit button.
      if (fields) {
        handleBraintreeValidation(fields, setCanSubmit);
        setHasCardNumber(Boolean(fields.number?.isValid));
        setHasExpDate(Boolean(fields.expirationDate?.isValid));
        setHasCvv(Boolean(fields.cvv?.isValid));
        setHasPostalCode(Boolean(fields.postalCode?.isValid));
      }

      // Nonce generated by the tokenize card function from braintree.
      if (receivedNonce) onSuccess(receivedNonce);
    },
    [handleBraintreeError, onSuccess],
  );

  return useMemo(
    () => ({
      isLoadingFields,
      isTokenizing,
      hasCardNumber,
      hasExpDate,
      hasCvv,
      hasPostalCode,
      canSubmit,
      handleBraintreeMessages,
    }),
    [
      isLoadingFields,
      isTokenizing,
      hasCardNumber,
      hasExpDate,
      hasCvv,
      hasPostalCode,
      canSubmit,
      handleBraintreeMessages,
    ],
  );
};

const useBraintreeErrorHandling = ({
  onError,
}: Pick<BraintreeMessagesHandlerProps, 'onError'>) => {
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  return useCallback(
    (error: { readonly message?: string }) => {
      logger.error(error);

      addNoticeBanner({
        text: t('credit-card-form.error.save'),
        palette: 'caution',
      });

      onError(t('credit-card-form.error.save'), `Braintree: ${error?.message}`);
    },
    [t, addNoticeBanner, onError],
  );
};

const parseBraintreeMessage = (event: WebViewMessageEvent) => {
  const dataString = event.nativeEvent.data ?? '{}';
  const parsedData = JSON.parse(dataString) as HostedFieldsPayload;

  return parsedData;
};

const handleBraintreeValidation = (
  fields: Record<string, HostedFieldValidity>,
  setCanSubmit: (isValid: boolean) => void,
) => {
  const values = Object.values(fields);
  const isValid = values.every((field) => field?.isValid);

  setCanSubmit(isValid);
};

// ─── Types ─────────────────────────────────────────────────────────────────────

type BraintreeMessagesHandlerProps = {
  readonly isEditing?: boolean;
  readonly onError: (userError?: string, systemError?: string) => void;
  readonly onSuccess: (nonce: string) => void;
};

type HostedFieldValidity = {
  readonly isValid: boolean;
};
type HostedFieldsPayload = {
  readonly nonce?: string;
  readonly error?: { readonly message?: string };
  readonly loaded?: boolean;
  readonly saving?: boolean;
  readonly fields?: Record<string, HostedFieldValidity>;
  readonly log?: string;
};
